import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api/axios';

export default function StationSelfiePage() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const stationParam = searchParams.get('station') || searchParams.get('estacion') || 'Estación Circuito';

  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  const [stationName, setStationName] = useState(stationParam);
  const [cameras, setCameras] = useState([]);
  const [selectedCameraId, setSelectedCameraId] = useState('');
  const [cameraQuality, setCameraQuality] = useState('hd'); // 'sd', 'hd', 'fhd'
  const [hasPermission, setHasPermission] = useState(false);
  const [stream, setStream] = useState(null);
  const [capturedImage, setCapturedImage] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [pointsMsg, setPointsMsg] = useState('');

  // Fullscreen Camera and Instructions modal states
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [isInstructionsOpen, setIsInstructionsOpen] = useState(false);

  const FTP_BASE = 'https://ftp.pcnpublicidad.com/boehringer/concentrese';

  useEffect(() => {
    if (stationParam) {
      resolveStationName(stationParam);
    }
  }, [stationParam]);

  const resolveStationName = async (param) => {
    try {
      const res = await api.get('/stations');
      const found = res.data.find(
        st => st.id.toString() === param ||
              st.code?.toLowerCase() === param.toLowerCase() ||
              st.name?.toLowerCase() === param.toLowerCase()
      );
      if (found) {
        setStationName(found.name);
      } else {
        setStationName(param);
      }
    } catch (err) {
      console.error('Error al resolver estación:', err);
      setStationName(param);
    }
  };

  const getQualityConstraints = (quality) => {
    switch (quality) {
      case 'fhd':
        return { width: { ideal: 1920 }, height: { ideal: 1080 } };
      case 'hd':
        return { width: { ideal: 1280 }, height: { ideal: 720 } };
      case 'sd':
      default:
        return { width: { ideal: 640 }, height: { ideal: 480 } };
    }
  };

  const requestCameraPermission = async (deviceId = null, quality = cameraQuality) => {
    setErrorMsg('');
    try {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }

      const qualityConstraints = getQualityConstraints(quality);
      const videoConstraints = deviceId
        ? { deviceId: { exact: deviceId }, ...qualityConstraints }
        : { facingMode: 'user', ...qualityConstraints };

      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: videoConstraints,
        audio: false
      });

      setStream(mediaStream);
      setHasPermission(true);
      setIsCameraOpen(true); // Open fullscreen camera automatically when permissions are granted

      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }

      // Enumerate available camera devices
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter(d => d.kind === 'videoinput');
      setCameras(videoDevices);
      if (!selectedCameraId && videoDevices.length > 0) {
        setSelectedCameraId(videoDevices[0].deviceId);
      }
    } catch (err) {
      console.error('Error al acceder a la cámara:', err);
      setHasPermission(false);
      setIsCameraOpen(false);
      setErrorMsg('No se pudo acceder a la cámara. Por favor asegúrese de otorgar los permisos requeridos.');
    }
  };

  // Re-bind video srcObject when videoRef attaches inside fullscreen modal
  useEffect(() => {
    if (isCameraOpen && stream && videoRef.current) {
      videoRef.current.srcObject = stream;
    }
  }, [isCameraOpen, stream]);

  useEffect(() => {
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, [stream]);

  const handleCameraChange = (e) => {
    const devId = e.target.value;
    setSelectedCameraId(devId);
    requestCameraPermission(devId, cameraQuality);
  };

  const handleQualityChange = (e) => {
    const quality = e.target.value;
    setCameraQuality(quality);
    requestCameraPermission(selectedCameraId, quality);
  };

  const takePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    const width = video.videoWidth || 1280;
    const height = video.videoHeight || 720;

    canvas.width = width;
    canvas.height = height;

    // Draw frame from camera stream
    ctx.drawImage(video, 0, 0, width, height);

    // Apply Event Branding Watermark Banner at the bottom
    const bannerHeight = Math.max(60, Math.round(height * 0.12));
    ctx.fillStyle = 'rgba(7, 36, 28, 0.85)'; // Boehringer Dark Green overlay
    ctx.fillRect(0, height - bannerHeight, width, bannerHeight);

    // Border line neon
    ctx.fillStyle = '#00e676';
    ctx.fillRect(0, height - bannerHeight, width, 4);

    // Event Branding Text
    ctx.fillStyle = '#ffffff';
    ctx.font = `bold ${Math.round(bannerHeight * 0.3)}px sans-serif`;
    ctx.fillText('FÓRMULA 1 — BOEHRINGER INGELHEIM', 20, height - (bannerHeight * 0.5));

    // Resolve robust user full name
    const userLastName = user ? (user.last_name || user.lastName || user.apellido || '') : '';
    const rawFullName = user ? `${user.name || ''} ${userLastName}`.trim() : '';
    const userFullName = rawFullName || 'PILOTO F1';

    // Station & User Tag
    ctx.fillStyle = '#00e676';
    ctx.font = `bold ${Math.round(bannerHeight * 0.22)}px monospace`;
    const stationTag = stationName ? stationName.toUpperCase() : 'ESTACIÓN CIRCUITO';
    ctx.fillText(`${stationTag} | ${userFullName.toUpperCase()}`, 20, height - (bannerHeight * 0.2));

    const dataUrl = canvas.toDataURL('image/png');
    setCapturedImage(dataUrl);
  };

  const sanitizeName = (str) => {
    return (str || '')
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]/g, '_')
      .replace(/_+/g, '_');
  };

  const uploadPhoto = async () => {
    if (!capturedImage) return;

    setIsUploading(true);
    setErrorMsg('');
    setPointsMsg('');
    try {
      // Convert base64 dataUrl to File blob
      const res = await fetch(capturedImage);
      const blob = await res.blob();

      const userLastName = user ? (user.last_name || user.lastName || user.apellido || '') : '';
      const rawFullName = user ? `${user.name || ''} ${userLastName}`.trim() : 'usuario';

      const stationNameClean = sanitizeName(stationName || 'estacion');
      const userNameClean = sanitizeName(rawFullName);
      const fileName = `${stationNameClean}_${userNameClean}.png`;

      const file = new File([blob], fileName, { type: 'image/png' });

      const formData = new FormData();
      formData.append('image', file);
      formData.append('station', stationName || stationParam);

      const response = await api.post('/gallery/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data?.pointsMessage) {
        setPointsMsg(response.data.pointsMessage);
      }

      setUploadSuccess(true);
      setIsCameraOpen(false); // Close camera modal upon successful upload
      setTimeout(() => {
        setCapturedImage(null);
        setUploadSuccess(false);
        setPointsMsg('');
      }, 4000);
    } catch (err) {
      console.error('Error al subir selfie al servidor:', err);
      setErrorMsg('Ocurrió un error al guardar la imagen en la galería del servidor.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleOpenFullscreenCamera = () => {
    if (!hasPermission) {
      requestCameraPermission(selectedCameraId, cameraQuality);
    } else {
      setIsCameraOpen(true);
    }
  };

  return (
    <div
      className="min-h-screen bg-[#07241c] text-white flex flex-col items-center p-4 sm:p-6 space-y-6 bg-cover bg-center animate-fadeIn"
      style={{ backgroundImage: `url(${FTP_BASE}/fondo.png)` }}
    >
      <div className="w-full max-w-2xl space-y-6">
        {/* Header */}
        <div className="bg-[#0b382c]/90 border-2 border-[#00e676]/50 rounded-3xl p-6 sm:p-8 shadow-2xl space-y-3 backdrop-blur-md text-center relative">
          <div className="flex items-center justify-between gap-2">
            <span className="bg-[#00e676]/20 text-[#00e676] border border-[#00e676]/40 text-[10px] sm:text-xs font-black px-4 py-1.5 rounded-full uppercase tracking-widest inline-block">
              Fotografía de Recorrido
            </span>
            <button
              onClick={() => setIsInstructionsOpen(true)}
              className="bg-[#07241c] hover:bg-[#00e676] text-[#00e676] hover:text-[#07241c] border border-[#00e676]/40 px-3 py-1.5 rounded-full text-xs font-bold transition-all flex items-center gap-1 shadow-md"
            >
              <span>ℹ️</span> Instrucciones
            </button>
          </div>

          <h1 className="text-2xl sm:text-3xl font-black text-white tracking-tight uppercase mt-2">
            Tomar Selfie — {stationName}
          </h1>
          <p className="text-xs sm:text-sm text-slate-200 font-medium leading-relaxed max-w-lg mx-auto">
            Inmortalice su experiencia en la estación <strong className="text-[#00e676]">{stationName}</strong>. La fotografía incluirá la marca oficial del evento.
          </p>
        </div>

        {/* Controls Card */}
        <div className="bg-[#0b382c]/90 rounded-3xl p-6 shadow-2xl border-4 border-[#00e676]/60 backdrop-blur-md space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Camera Selector */}
            <div>
              <label className="block text-[10px] font-black text-[#00e676] uppercase tracking-widest mb-1.5">
                Cámara Disponible
              </label>
              <select
                value={selectedCameraId}
                onChange={handleCameraChange}
                disabled={cameras.length === 0}
                className="w-full bg-[#07241c] border border-[#00e676]/40 text-white text-xs rounded-xl p-3 font-bold focus:outline-none disabled:opacity-50"
              >
                {cameras.length > 0 ? (
                  cameras.map((cam, idx) => (
                    <option key={cam.deviceId} value={cam.deviceId}>
                      {cam.label || `Cámara ${idx + 1}`}
                    </option>
                  ))
                ) : (
                  <option value="">Seleccionar Cámara</option>
                )}
              </select>
            </div>

            {/* Camera Quality Selector */}
            <div>
              <label className="block text-[10px] font-black text-[#00e676] uppercase tracking-widest mb-1.5">
                Calidad de Imagen
              </label>
              <select
                value={cameraQuality}
                onChange={handleQualityChange}
                className="w-full bg-[#07241c] border border-[#00e676]/40 text-white text-xs rounded-xl p-3 font-bold focus:outline-none"
              >
                <option value="sd">Estándar (SD - 640x480)</option>
                <option value="hd">Alta Definición (HD - 1280x720)</option>
                <option value="fhd">Full HD (FHD - 1920x1080)</option>
              </select>
            </div>
          </div>

          {/* Main Action Button for Fullscreen Camera */}
          <button
            onClick={handleOpenFullscreenCamera}
            className="w-full py-4 bg-[#00e676] hover:bg-[#00c853] text-[#07241c] font-black text-sm uppercase tracking-widest rounded-2xl shadow-xl hover:scale-[1.02] active:scale-95 transition-all flex items-center justify-center gap-3 cursor-pointer"
          >
            <span className="text-xl">📷</span>
            <span>{hasPermission ? 'Abrir Cámara a Pantalla Completa' : 'Activar Permisos y Abrir Cámara'}</span>
          </button>

          {errorMsg && (
            <div className="bg-red-500/20 border border-red-500/40 text-red-200 text-xs p-3 rounded-xl text-center font-semibold">
              {errorMsg}
            </div>
          )}
        </div>

        {/* Saved Photo Preview (If photo already taken) */}
        {capturedImage && !isCameraOpen && (
          <div className="bg-[#0b382c]/90 rounded-3xl p-6 shadow-2xl border-4 border-[#00e676]/60 backdrop-blur-md flex flex-col items-center space-y-4">
            <h3 className="text-xs font-black uppercase tracking-widest text-[#00e676]">
              Última Fotografía Capturada
            </h3>
            <div className="w-full aspect-[4/3] bg-black rounded-2xl overflow-hidden border-2 border-[#00e676]/40 shadow-md">
              <img src={capturedImage} alt="Selfie Capturado" className="w-full h-full object-contain" />
            </div>
            <div className="w-full flex gap-3">
              <button
                onClick={() => {
                  setCapturedImage(null);
                  setIsCameraOpen(true);
                }}
                disabled={isUploading}
                className="flex-1 py-3.5 bg-[#07241c] border border-slate-500 text-slate-200 font-bold text-xs uppercase tracking-wider rounded-2xl hover:bg-slate-800 transition-all cursor-pointer"
              >
                🔄 Repetir Foto
              </button>
              <button
                onClick={uploadPhoto}
                disabled={isUploading}
                className="flex-1 py-3.5 bg-[#00e676] hover:bg-[#00c853] text-[#07241c] font-black text-xs uppercase tracking-widest rounded-2xl shadow-xl transition-all flex items-center justify-center gap-2 cursor-pointer"
              >
                {isUploading ? 'GUARDANDO...' : '💾 GUARDAR EN SERVIDOR'}
              </button>
            </div>
          </div>
        )}

        {/* Global Notification Banner */}
        {uploadSuccess && (
          <div className="w-full bg-[#00e676]/20 border border-[#00e676]/60 text-[#00e676] text-xs font-black p-4 rounded-2xl text-center uppercase tracking-wider animate-fadeIn space-y-1">
            <div>✅ Fotografía almacenada exitosamente en el servidor</div>
            {pointsMsg && (
              <div className="text-[11px] text-amber-300 normal-case font-bold">{pointsMsg}</div>
            )}
          </div>
        )}
      </div>

      {/* Hidden Canvas element for photo rendering */}
      <canvas ref={canvasRef} className="hidden" />

      {/* FULLSCREEN CAMERA VISOR MODAL (SIMULATING NATIVE CAMERA APP) */}
      {isCameraOpen && createPortal(
        <div className="fixed inset-0 z-[99999] bg-black text-white flex flex-col justify-between overflow-hidden animate-fadeIn">
          {/* Top Bar: Close Button + Settings */}
          <div className="relative z-20 p-4 sm:p-6 bg-gradient-to-b from-black/80 via-black/40 to-transparent flex items-center justify-between gap-4">
            <div className="flex items-center gap-2 bg-black/50 backdrop-blur-md px-3 py-1.5 rounded-full border border-white/20">
              <span className="w-2.5 h-2.5 rounded-full bg-red-500 animate-ping"></span>
              <span className="text-[10px] font-black uppercase tracking-wider text-white">
                {stationName}
              </span>
            </div>

            {/* Camera Switcher Dropdown in top bar if multiple cameras exist */}
            {cameras.length > 1 && (
              <select
                value={selectedCameraId}
                onChange={handleCameraChange}
                className="bg-black/60 border border-white/30 text-white text-[10px] font-bold rounded-full px-3 py-1 focus:outline-none"
              >
                {cameras.map((cam, idx) => (
                  <option key={cam.deviceId} value={cam.deviceId}>
                    📷 {cam.label || `Cámara ${idx + 1}`}
                  </option>
                ))}
              </select>
            )}

            {/* Close Button */}
            <button
              onClick={() => setIsCameraOpen(false)}
              className="w-11 h-11 bg-white/20 hover:bg-white/30 text-white rounded-full flex items-center justify-center text-xl font-bold transition-all backdrop-blur-md active:scale-90 cursor-pointer"
              title="Cerrar cámara"
            >
              ✕
            </button>
          </div>

          {/* Center Viewfinder: Video Stream or Captured Preview */}
          <div className="relative flex-1 w-full h-full bg-black flex items-center justify-center overflow-hidden">
            {capturedImage ? (
              <img
                src={capturedImage}
                alt="Vista Previa Selfie"
                className="w-full h-full object-contain"
              />
            ) : (
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="w-full h-full object-cover"
              />
            )}

            {/* Smartphone Viewfinder Framing Overlay */}
            {!capturedImage && (
              <div className="absolute inset-8 sm:inset-16 border-2 border-white/30 rounded-3xl pointer-events-none flex flex-col justify-between p-4 shadow-[0_0_0_9999px_rgba(0,0,0,0.3)]">
                <div className="flex justify-between">
                  <div className="w-6 h-6 border-t-4 border-l-4 border-[#00e676] rounded-tl-lg"></div>
                  <div className="w-6 h-6 border-t-4 border-r-4 border-[#00e676] rounded-tr-lg"></div>
                </div>
                <div className="text-center text-[10px] font-black uppercase tracking-widest text-[#00e676] bg-black/60 backdrop-blur-md px-4 py-1 rounded-full self-center">
                  Encuadra tu rostro
                </div>
                <div className="flex justify-between">
                  <div className="w-b-6 h-6 border-b-4 border-l-4 border-[#00e676] rounded-bl-lg"></div>
                  <div className="w-6 h-6 border-b-4 border-r-4 border-[#00e676] rounded-br-lg"></div>
                </div>
              </div>
            )}
          </div>

          {/* Bottom Bar: Shutter Controls or Confirm Actions */}
          <div className="relative z-20 p-6 sm:p-8 bg-gradient-to-t from-black/90 via-black/60 to-transparent flex flex-col items-center justify-center gap-4">
            {capturedImage ? (
              <div className="w-full max-w-md flex gap-4">
                <button
                  onClick={() => setCapturedImage(null)}
                  disabled={isUploading}
                  className="flex-1 py-4 bg-slate-800 hover:bg-slate-700 text-white font-bold text-xs uppercase tracking-wider rounded-2xl transition-all border border-white/20 active:scale-95 cursor-pointer"
                >
                  🔄 Volver a Tomar
                </button>
                <button
                  onClick={uploadPhoto}
                  disabled={isUploading}
                  className="flex-1 py-4 bg-[#00e676] hover:bg-[#00c853] text-[#07241c] font-black text-xs uppercase tracking-widest rounded-2xl shadow-2xl transition-all flex items-center justify-center gap-2 active:scale-95 cursor-pointer"
                >
                  {isUploading ? 'GUARDANDO...' : '💾 GUARDAR SELFIE'}
                </button>
              </div>
            ) : (
              <div className="flex items-center justify-center gap-8 w-full max-w-xs">
                {/* Big Native Smartphone Camera Shutter Button */}
                <button
                  onClick={takePhoto}
                  className="w-20 h-20 rounded-full border-4 border-white bg-white/20 p-1 flex items-center justify-center transition-all hover:scale-105 active:scale-90 shadow-2xl cursor-pointer"
                  title="Capturar foto"
                >
                  <span className="w-full h-full rounded-full bg-[#00e676] border-2 border-white shadow-inner flex items-center justify-center text-2xl">
                    📸
                  </span>
                </button>
              </div>
            )}
          </div>
        </div>,
        document.body
      )}

      {/* SELFIE USAGE INSTRUCTIONS MODAL */}
      {isInstructionsOpen && createPortal(
        <div className="fixed inset-0 z-[99999] bg-black/85 backdrop-blur-md flex items-center justify-center p-4 animate-fadeIn">
          <div className="bg-[#0b382c] border-2 border-[#00e676] rounded-3xl p-6 sm:p-8 max-w-md w-full space-y-5 shadow-2xl text-left relative">
            <button
              onClick={() => setIsInstructionsOpen(false)}
              className="absolute top-5 right-5 text-white/60 hover:text-white text-xl font-bold"
            >
              ✕
            </button>

            <div className="flex items-center gap-3 border-b border-[#00e676]/30 pb-3">
              <span className="text-3xl">📸</span>
              <div>
                <h3 className="text-lg font-black text-[#00e676] uppercase tracking-wider">
                  Instrucciones para tu Selfie
                </h3>
                <p className="text-[10px] text-slate-300 font-bold uppercase tracking-wider">
                  {stationName}
                </p>
              </div>
            </div>

            <div className="space-y-3 text-xs text-slate-200">
              <div className="flex items-start gap-3 bg-[#07241c] p-3 rounded-2xl border border-[#00e676]/20">
                <span className="bg-[#00e676] text-[#07241c] font-black w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-xs">
                  1
                </span>
                <p className="leading-relaxed">
                  Haz clic en <strong>"Activar Permisos y Abrir Cámara"</strong> para desplegar la cámara a pantalla completa.
                </p>
              </div>

              <div className="flex items-start gap-3 bg-[#07241c] p-3 rounded-2xl border border-[#00e676]/20">
                <span className="bg-[#00e676] text-[#07241c] font-black w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-xs">
                  2
                </span>
                <p className="leading-relaxed">
                  Ubícate frente a la cámara en un lugar iluminado y encuadra tu rostro dentro del marco guía.
                </p>
              </div>

              <div className="flex items-start gap-3 bg-[#07241c] p-3 rounded-2xl border border-[#00e676]/20">
                <span className="bg-[#00e676] text-[#07241c] font-black w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-xs">
                  3
                </span>
                <p className="leading-relaxed">
                  Presiona el botón obturador 📸 para tomar la foto y haz clic en <strong>"Guardar Selfie"</strong> para registrar tu puntuación en la estación.
                </p>
              </div>
            </div>

            <button
              onClick={() => setIsInstructionsOpen(false)}
              className="w-full bg-[#00e676] text-[#07241c] py-3.5 px-6 rounded-2xl font-black uppercase tracking-widest text-xs shadow-lg hover:scale-105 active:scale-95 transition-all text-center cursor-pointer mt-2"
            >
              ENTENDIDO
            </button>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

